import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";
import parse, { domToReact } from "html-react-parser";
import Icon from "./icon";
import Document from "./document";
import { parseFile } from "../utils/documents";
import UserContext from "../context/user";

const FileInput = ({ name, field, feedback, required, maxFiles, disabled, labels, trigger }) => {
  const { onChange: controlledOnChange } = field;
  const { callApi } = useContext(UserContext);
  const isLoaded = useRef();
  const [files, setFiles] = useState();
  const [isUploading, setIsUploading] = useState();
  const [downloadDisabled, setDownloadDisabled] = useState(false);

  const isMultiple = maxFiles < 0;

  const filesFromForm = Array.isArray(field?.value) ? field.value?.length > 0 : !!field?.value;

  const detailResponses = useQueries({
    queries: (Array.isArray(field?.value)
      ? field?.value
      : field.value === undefined
      ? []
      : [field.value]
    )?.map((fileId) => ({
      queryKey: ["document", fileId],
      queryFn: () => callApi(`/survey/documents/${fileId}/detail`),
      enabled: filesFromForm && !!fileId && !downloadDisabled,
    })),
  });

  const allFinished = detailResponses?.every((query) => query.isSuccess);

  useEffect(() => {
    if (filesFromForm && allFinished && !downloadDisabled) {
      setFiles(detailResponses?.map(({ data }) => parseFile({ name: data.fileName, ...data })));
      setDownloadDisabled(true);
    }
  }, [allFinished, detailResponses, downloadDisabled, filesFromForm]);

  const isFetching = useMemo(
    () => detailResponses?.some((response) => response?.isFetching),
    [detailResponses]
  );
  const error = useMemo(
    () => detailResponses?.some((response) => response?.error),
    [detailResponses]
  );

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setIsUploading(true);
      isLoaded.current = true;
      console.log("Accepted files:", acceptedFiles);
      if (acceptedFiles?.length > 0) {
        const files = isMultiple ? acceptedFiles : [acceptedFiles[0]];
        console.log("Uploading", files);
        try {
          const results = await Promise.all(
            files.map(async (file) => {
              const formData = new FormData();
              formData.append("file", file);
              try {
                const result = await callApi("/survey/documents/upload", {
                  method: "POST",
                  body: formData,
                });
                return { file, result };
              } catch (err) {
                console.error("Error uploading file", file.name);
              }
            })
          );
          console.log("Uploaded", results);
          setFiles((current) =>
            (current || []).concat(results?.map(({ file, result }) => parseFile(file, result)))
          );
        } catch (err) {
          console.error("Error in file upload: ", err);
        }
      }
      setIsUploading(false);
    },
    [callApi, isMultiple]
  );

  useEffect(() => {
    if (files) {
      controlledOnChange(files?.map((file) => file.id));
    }
  }, [files, controlledOnChange]);

  const {
    open,
    getRootProps,
    getInputProps,
    isDragActive,
    fileRejections,
    isFocused,
    isFileDialogActive,
  } = useDropzone({
    onDrop,
    useFsAccessApi: false,
    maxSize: 1e7,
    ...(maxFiles ? { maxFiles: 20 } : {}),
    disabled: disabled,
  });

  const dragAndDropLabel = isDragActive
    ? labels?.WELFARE_DROP_FILES_HERE || "Drop files here"
    : parse(
        labels?.WELFARE_DRAG_AND_DROP_OR_CLICK_HERE ||
          "Drop your file here, or <button>click here</button>",
        {
          replace: (elem) => {
            if (elem.type === "tag" && elem.name === "button") {
              return (
                <button
                  onClick={open}
                  type="button"
                  className="link link--accent"
                  disabled={disabled}
                >
                  {domToReact(elem.children)}
                </button>
              );
            }
          },
        }
      );

  const removeDoc = (fileId) => {
    setFiles((current) => (current || []).filter((file) => file.id !== fileId));
  };

  return (
    <>
      {error ? (
        <div className="form__error-msg">
          {labels?.WELFARE_ERROR_RETRIEVING_FILES || "An error occurred retrieving your uploads."}
        </div>
      ) : (
        <>
          {(isMultiple ? true : files?.length === 0 || !files) && (
            <div
              {...getRootProps()}
              onClick={(e) => e.preventDefault()}
              onBlur={(e) => {
                if (!isFileDialogActive) {
                  trigger(name);
                  getRootProps().onBlur(e);
                }
              }}
              role="region"
              aria-label="Upload file"
              tabIndex={1}
              className={classNames(
                "form__dropzone-wrapper",
                (isFocused || isFileDialogActive) && "active",
                disabled && "disabled"
              )}
            >
              <input {...getInputProps({ onChange: onDrop })} required={required} name="file" />
              <div className="form__dropzone">
                <Icon name="upload" />
                {isUploading ? <span className="loading-spinner" /> : <p>{dragAndDropLabel}</p>}
                <span className="form__dropzone-tooltip">
                  {labels?.WELFARE_MAX_FILE_SIZE_10MB || "Maximum file size: 10MB"}
                </span>
              </div>
            </div>
          )}
          {!!fileRejections?.length && fileRejections[0].errors?.[0] && (
            <p className="form__error-msg">
              {fileRejections[0].errors[0].code === "file-too-large" &&
                (labels?.WELFARE_FILE_TOO_LARGE_10_MB || "The provided file is too large.")}
            </p>
          )}
          <span className="form__input-button">
            {feedback === "error" ? <Icon name="error" /> : <Icon name="check" />}
          </span>
          {files &&
            (isFetching ? (
              <div className="loading-spinner" />
            ) : (
              files?.length > 0 && (
                <div className="form__files">
                  {files?.map((file, i) => (
                    <Document
                      key={i}
                      doc={file}
                      removable={!disabled}
                      onRemove={() => removeDoc(file.id)}
                      disabled={disabled}
                    />
                  ))}
                </div>
              )
            ))}
        </>
      )}
    </>
  );
};

export default FileInput;
